define("shared/utils/array-methods", ["exports", "@ember/utils"], function (_exports, _utils) {
  "use strict";

  Object.defineProperty(_exports, "__esModule", {
    value: true
  });
  _exports.transpose = _exports.sumArray = _exports.standardDeviation = _exports.sortSelectionToFront = _exports.randomizeArray = _exports.generateRangedArray = _exports.findUnique = _exports.deepEqualsArray = _exports.calculateMax = _exports.calculateAverage = _exports.arrayIntersection = _exports.arrayDiff = void 0;
  0; //eaimeta@70e063a35619d71f0,"@ember/utils"eaimeta@70e063a35619d71f
  /**
   * @module Utils
   */
  /**
   * @class arrayMethods
   * @extends Utils
   */
  /**
   * Takes a unique array, and a selected value and sorts
   * that selection to the front of the array.
   * Note if your array is not unique, it will return the unsorted array.
   *
   * @method sortSelectionToFront
   * @param {Array} arr Array of unique values
   * @param {String|Number} selectedValue Value to be sorted to the front
   *
   * @returns {Array} Sorted array
   */
  const sortSelectionToFront = (arr, selectedValue) => {
    const filteredArray = arr.filter(value => value !== selectedValue);
    if (arr.length - filteredArray.length === 1) {
      return [selectedValue, ...filteredArray];
    }
    return arr;
  };

  /**
   * Randomly shuffle an array
   * https://gomakethings.com/how-to-shuffle-an-array-with-vanilla-js/
   * @method randomizeArray
   * @param {Array} arr The array to shuffle
   *
   * @return {Array} The shuffled array
   */
  _exports.sortSelectionToFront = sortSelectionToFront;
  const randomizeArray = arr => {
    let currentIndex = arr.length;
    let temporaryValue;
    let randomIndex;
    while (0 !== currentIndex) {
      randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex -= 1;
      temporaryValue = arr[currentIndex];
      arr[currentIndex] = arr[randomIndex];
      arr[randomIndex] = temporaryValue;
    }
    return arr;
  };

  /**
   * Returns the sum of an array of numbers.
   *
   * @method sumArray
   * @param {Array} values Array of numbers, which are to be summed
   *
   * @returns {Number} Sum of values
   */
  _exports.randomizeArray = randomizeArray;
  const sumArray = values => {
    const filteredValues = values.filter(value => (0, _utils.isPresent)(value));
    if (!filteredValues || filteredValues.length === 0) {
      return null;
    }
    return filteredValues.reduce((accumulator, filteredValue) => (accumulator || 0) + (filteredValue || 0), 0);
  };

  /**
   * Returns a unique array of entries
   *
   * @method findUnique
   * @param {Array} arr Array of values
   *
   * @returns {Array} Unique array of values
   */
  _exports.sumArray = sumArray;
  const findUnique = arr => [...new Set(arr)];

  /**
   * Takes a matrix, which is an array of column
   * objects and returns an array of rows.
   *
   * @method transpose
   * @param {Array.<Object>} matrix Array of objects (columns)
   *
   * @returns {Array.<Array>} Array of arrays (rows)
   */
  _exports.findUnique = findUnique;
  const transpose = matrix => Object.keys(matrix[0]).map(column => matrix.map(row => row[column]));

  /**
   * Returns the average value of an array of numbers.
   *
   * @method calculateAverage
   * @param {Array} values Array of numbers, which are to be averaged using @sumData
   *
   * @returns {Number} Average value of values
   */
  _exports.transpose = transpose;
  const calculateAverage = values => {
    if (!values || values.length === 0) {
      return 0;
    }
    const sum = sumArray(values);
    return sum / values.length;
  };

  /**
   * Returns the maximum value of an array of numbers.
   *
   * @method calculateMax
   * @param {Array} values Array of numbers
   *
   * @returns {Number} Max value of values
   */
  _exports.calculateAverage = calculateAverage;
  const calculateMax = values => {
    if (!values || values.length === 0) {
      return 0;
    }
    return values.sort((prev, curr) => curr - prev)[0];
  };

  /**
   * Returns the standard deviation (STD) for an array of numbers.
   *
   * @method standardDeviation
   * @param {Array} values Array of numbers, used to calculate STD
   *
   * @returns {Number} Standard deviation for the array
   */
  _exports.calculateMax = calculateMax;
  const standardDeviation = values => {
    const average = calculateAverage(values);
    const squareDiffs = values.map(value => {
      const diff = value - average;
      return diff * diff;
    });
    const averageSquareDiff = calculateAverage(squareDiffs);
    return Math.sqrt(averageSquareDiff);
  };

  /**
   * Compares two arrays
   *
   * @method deepEqualsArray
   * @param {Array} arr1 The first array to compare to the second array
   * @param {Array} arr2 The second array to compare to the first array
   *
   * @return {Boolean} Whether or not the arrays are equal
   */
  _exports.standardDeviation = standardDeviation;
  const deepEqualsArray = (arr1, arr2) => JSON.stringify(arr1) === JSON.stringify(arr2);

  /**
   * Generate a ranged array of given length, with given offset.
   *
   * @method deepEqualsArray
   * @param {Integer} length The desired length of the array
   * @param {Integer} [offset=0] The starting value for the range
   *
   * @return {Array} Ranged array
   */
  _exports.deepEqualsArray = deepEqualsArray;
  const generateRangedArray = function (length) {
    let offset = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
    return [...Array(length).keys()].map(key => key + offset);
  };

  /**
   * Returns an array of values that only appear
   * in given array a and not in array b
   *
   * @method arrayDiff
   * @param a The array to filter out values
   * @param b The array of values to check against
   *
   * @return {Array} An array of values from array a that do not appear in array b
   * @protected
   */
  _exports.generateRangedArray = generateRangedArray;
  const arrayDiff = (a, b) => a.filter(i => !b.includes(i));

  /**
   * Returns an array of values that appear in both
   * array a and array b
   *
   * @method arrayIntersection
   * @param a first array
   * @param b second array
   *
   * @return {Array} An array of values that appear in both a and b
   * @protected
   */
  _exports.arrayDiff = arrayDiff;
  const arrayIntersection = (a, b) => a.filter(i => b.includes(i));
  _exports.arrayIntersection = arrayIntersection;
});